import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as GoogleLogo } from '../../icons/google-logo.svg';
import outlookLogo from '../../icons/outlook.png';
import {
  AccountSlot,
  ConnectedAccount,
  connectUrl,
  disconnectAccount,
  patchWizardState,
} from './wizardApi';
import {
  ExternalArrowIcon,
  MinusCircleIcon,
  WarnTriangleIcon,
} from './wizardIcons';

const ADMIN_GUIDE_URL = 'https://google-access-slides.vercel.app';

type Provider = 'google' | 'outlook';

interface ConnectStepProps {
  token: string;
  assistantName: string;
  accounts: ConnectedAccount[];
  workConnectAttempted: boolean;
  oauthNotice: string | null;
  oauthNoticeTitle?: string | null;
  onDismissNotice: () => void;
  onError: (message: string) => void;
  onAccountsChanged: () => void;
  /** Organisation name when a work account is required to carry on. */
  workRequiredFor?: string | null;
}

const CONSUMER_EMAIL_DOMAINS = new Set([
  'gmail.com',
  'googlemail.com',
  'outlook.com',
  'hotmail.com',
  'hotmail.co.uk',
  'live.com',
  'live.co.uk',
  'msn.com',
  'yahoo.com',
  'yahoo.co.uk',
  'icloud.com',
  'me.com',
  'mac.com',
  'aol.com',
  'protonmail.com',
  'proton.me',
  'btinternet.com',
  'sky.com',
  'virginmedia.com',
]);

/** Accounts with no slot recorded (connected before the wizard) are filed by
 * address, the same way the server counts them. */
export function displaySlot(account: ConnectedAccount): AccountSlot {
  if (account.slot) return account.slot;
  const domain = (account.identifier ?? '').split('@')[1]?.trim().toLowerCase();
  return !domain || CONSUMER_EMAIL_DOMAINS.has(domain) ? 'personal' : 'work';
}

export function accountsForSlot(
  accounts: ConnectedAccount[],
  slot: AccountSlot,
): ConnectedAccount[] {
  return accounts.filter((account) => displaySlot(account) === slot);
}

const providerGlyph = (provider: string) =>
  provider === 'MICROSOFT' ? (
    <img className='glyph' src={outlookLogo} alt='' aria-hidden='true' />
  ) : (
    <GoogleLogo className='glyph' aria-hidden='true' />
  );

const ConnectStep: React.FC<ConnectStepProps> = ({
  token,
  assistantName,
  accounts,
  workConnectAttempted,
  oauthNotice,
  oauthNoticeTitle,
  onDismissNotice,
  onError,
  onAccountsChanged,
  workRequiredFor,
}) => {
  const { t } = useTranslation();
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [busyAccountId, setBusyAccountId] = useState<number | null>(null);
  const [adminNoteDismissed, setAdminNoteDismissed] = useState(false);
  const [guideLinkCopied, setGuideLinkCopied] = useState(false);
  const [otherPicked, setOtherPicked] = useState(false);

  const workAccounts = accountsForSlot(accounts, 'work');
  const showAdminNote =
    workConnectAttempted && workAccounts.length === 0 && !adminNoteDismissed;

  // The wizard often runs inside WhatsApp's in-app browser, which flattens
  // target="_blank" into an in-place navigation with no way back — so the
  // guide is shared (or copied) rather than navigated to.
  const shareAdminGuide = async () => {
    const shareData = {
      title: t('wizard.connect.adminGuideShareTitle'),
      url: ADMIN_GUIDE_URL,
    };
    if (
      typeof navigator.share === 'function' &&
      navigator.canShare?.(shareData)
    ) {
      try {
        await navigator.share(shareData);
        return;
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          return;
        }
      }
    }
    try {
      await navigator.clipboard.writeText(ADMIN_GUIDE_URL);
      setGuideLinkCopied(true);
      window.setTimeout(() => setGuideLinkCopied(false), 2500);
    } catch {
      window.open(ADMIN_GUIDE_URL, '_blank', 'noopener');
    }
  };

  const startConnect = async (provider: Provider) => {
    setBusyKey(provider);
    try {
      if (workRequiredFor) {
        // Recorded before we leave the page so an abandoned attempt (OAuth
        // bounce with nothing connected) can be detected when they return.
        await patchWizardState(token, { workConnectAttempted: true });
      }
      window.location.assign(connectUrl(token, provider, 'auto'));
    } catch (error) {
      setBusyKey(null);
      onError(
        error instanceof Error
          ? error.message
          : t('wizard.connect.openConnectError'),
      );
    }
  };

  const handleSetSlot = async (oauthId: number, slot: AccountSlot) => {
    setBusyAccountId(oauthId);
    try {
      await patchWizardState(token, { accountSlot: { oauthId, slot } });
      onAccountsChanged();
    } catch (error) {
      onError(
        error instanceof Error
          ? error.message
          : t('wizard.connect.changeKindError'),
      );
    } finally {
      setBusyAccountId(null);
    }
  };

  const handleDisconnect = async (oauthId: number) => {
    setBusyAccountId(oauthId);
    try {
      await disconnectAccount(token, oauthId);
      onAccountsChanged();
    } catch (error) {
      onError(
        error instanceof Error
          ? error.message
          : t('wizard.connect.disconnectError'),
      );
    } finally {
      setBusyAccountId(null);
    }
  };

  const kindLabel = (slot: AccountSlot) =>
    slot === 'work'
      ? t('wizard.connect.professional')
      : t('wizard.connect.personal');

  const workNeeded = !!workRequiredFor && workAccounts.length === 0;

  return (
    <section className='screen active' data-screen='connect'>
      <h1 className='ob-title'>{t('wizard.connect.chooseTitle')}</h1>
      <p className='ob-lede'>
        {t('wizard.connect.chooseLede', { name: assistantName })}
      </p>

      {oauthNotice && (
        <div className='admin-note'>
          <WarnTriangleIcon />
          <div>
            <div className='admin-note-title'>
              {oauthNoticeTitle || t('wizard.connect.connectionDidntFinish')}
            </div>
            <p className='admin-note-body'>{oauthNotice}</p>
            <div className='admin-note-actions'>
              <button className='btn-quiet' onClick={onDismissNotice}>
                {t('wizard.connect.dismiss')}
              </button>
            </div>
          </div>
        </div>
      )}

      {accounts.length > 0 && (
        <div className='acct-list acct-connected'>
          {accounts.map((account) => {
            const slot = displaySlot(account);
            return (
              <div data-group={slot} key={account.id}>
                <div className='acct-row'>
                  {providerGlyph(account.type)}
                  <span className='acct-main'>
                    <span className='acct-email'>
                      {account.identifier ||
                        t('wizard.connect.connectedAccount')}
                    </span>
                    <span
                      className='acct-kind-switch'
                      role='group'
                      aria-label={t('wizard.connect.kindAria')}
                    >
                      {(['work', 'personal'] as AccountSlot[]).map((option) => (
                        <button
                          key={option}
                          type='button'
                          className={option === slot ? 'on' : ''}
                          aria-pressed={option === slot}
                          disabled={busyAccountId === account.id}
                          onClick={() =>
                            option !== slot && handleSetSlot(account.id, option)
                          }
                        >
                          {kindLabel(option)}
                        </button>
                      ))}
                    </span>
                  </span>
                  <button
                    className='acct-remove'
                    onClick={() => handleDisconnect(account.id)}
                    disabled={busyAccountId === account.id}
                    aria-label={t('wizard.connect.disconnectAria', {
                      account:
                        account.identifier || t('wizard.connect.thisAccount'),
                    })}
                  >
                    <MinusCircleIcon />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className='acct-providers'>
        {accounts.length > 0 && (
          <div className='acct-step-label'>
            {t('wizard.connect.addAnother')}
          </div>
        )}
        {workNeeded && (
          <p className='acct-other-note' role='note'>
            {t('wizard.connect.workNeededNote', { org: workRequiredFor })}
          </p>
        )}
        {(['google', 'outlook'] as Provider[]).map((provider) => (
          <button
            key={provider}
            className={`connect-btn${busyKey === provider ? ' busy' : ''}`}
            onClick={() => startConnect(provider)}
            disabled={busyKey !== null}
          >
            {provider === 'google' ? (
              <GoogleLogo className='glyph' aria-hidden='true' />
            ) : (
              <img
                className='glyph'
                src={outlookLogo}
                alt=''
                aria-hidden='true'
              />
            )}
            <span>
              {busyKey === provider
                ? t('wizard.connect.opening')
                : provider === 'google'
                  ? t('wizard.connect.gmail')
                  : t('wizard.connect.outlook')}
            </span>
          </button>
        ))}
        <button
          type='button'
          className={'provider-other' + (otherPicked ? ' on' : '')}
          onClick={() => setOtherPicked(true)}
        >
          {t('wizard.connect.other')}
        </button>
        {otherPicked && (
          <p className='acct-other-note' role='note'>
            {workRequiredFor
              ? t('wizard.connect.otherNoteWork', { org: workRequiredFor })
              : t('wizard.connect.otherNote', { name: assistantName })}
          </p>
        )}
      </div>

      {showAdminNote && (
        <div className='admin-note'>
          <WarnTriangleIcon />
          <div>
            <div className='admin-note-title'>
              {t('wizard.connect.adminNoteTitle')}
            </div>
            <p className='admin-note-body'>
              {t('wizard.connect.adminNoteBody')}
            </p>
            <div className='admin-note-actions'>
              <button className='inline-link' onClick={shareAdminGuide}>
                {guideLinkCopied
                  ? t('wizard.connect.guideLinkCopied')
                  : t('wizard.connect.sendAdminGuide')}
              </button>
              <a
                className='inline-link'
                href={ADMIN_GUIDE_URL}
                target='_blank'
                rel='noreferrer'
              >
                {t('wizard.connect.viewGuide')} <ExternalArrowIcon />
              </a>
              <button
                className='btn-quiet'
                onClick={() => setAdminNoteDismissed(true)}
              >
                {t('wizard.connect.dismiss')}
              </button>
            </div>
          </div>
        </div>
      )}

      <p className='reassure'>
        <svg
          viewBox='0 0 16 16'
          fill='none'
          stroke='currentColor'
          strokeWidth='1.6'
          strokeLinecap='round'
          strokeLinejoin='round'
          aria-hidden='true'
        >
          <path d='M8 1.8l5 2v4.1c0 3-2.1 5.4-5 6.3-2.9-.9-5-3.3-5-6.3V3.8z' />
          <path d='M5.9 8.1l1.5 1.5 3-3.2' />
        </svg>
        {t('wizard.connect.neverSend')}
      </p>
    </section>
  );
};

export default ConnectStep;
